import { useEffect, useState } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app, storage } from "../firebase";
import { ref, getDownloadURL } from "firebase/storage";
import { extractTextFromFile } from "../course/fileTextExtractor";
import {
    Settings,
    FileText,
    Brain,
    Download,
    Plus,
    Minus,
    Sparkles,
    BookOpen,
    Target,
    Palette,
    Zap,
    CheckCircle,
    Clock,
    Users,
    TrendingUp,
    FileType,
    FileDown,
    AlertCircle,
    Check,
    X,
    Trophy,
    Timer,
    Award,
    Hash,
    FileSpreadsheet,
    Gamepad2
} from "lucide-react";

/* =========================
   TYPES
========================= */
export type FileItem = {
    name: string;
    path: string;
};

export type KahootTaskCreatorProps = {
    defaultTopic?: string;
    files?: FileItem[];
};

/* =========================
   SUB COMPONENT - QuestionTypeCard (für Kahoot)
========================= */
interface QuestionTypeCardProps {
    label: string;
    description: string;
    value: number;
    onChange: (value: number) => void;
    color: string;
    icon: string;
}

function QuestionTypeCard({ label, description, value, onChange, color, icon }: QuestionTypeCardProps) {
    return (
        <div
            className="bg-gray-900/50 border-l-4 p-5 rounded-xl border border-gray-800 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/20 transition-all duration-300"
            style={{ borderLeftColor: color }}
        >
            <div className="flex items-start gap-3 mb-4">
                <span className="text-2xl">{icon}</span>
                <div className="flex-1">
                    <h4 className="text-base font-bold text-white mb-1">{label}</h4>
                    <p className="text-sm text-gray-400">{description}</p>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <button
                    onClick={() => onChange(Math.max(1, value - 1))}
                    disabled={value <= 1}
                    className="w-9 h-9 rounded-lg border-2 border-gray-700 bg-gray-950/80 flex items-center justify-center text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-purple-500 hover:text-purple-500 transition-colors"
                >
                    <Minus size={16} />
                </button>
                <input
                    type="number"
                    min={1}
                    max={20}
                    value={value}
                    onChange={(e) => {
                        const val = parseInt(e.target.value);
                        if (!isNaN(val)) onChange(Math.min(20, Math.max(1, val)));
                    }}
                    className="flex-1 py-2 text-center text-lg font-bold border-2 rounded-lg bg-gray-950/80 text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                    style={{ borderColor: color }}
                />
                <button
                    onClick={() => onChange(Math.min(20, value + 1))}
                    disabled={value >= 20}
                    className="w-9 h-9 rounded-lg border-2 border-gray-700 bg-gray-950/80 flex items-center justify-center text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:border-purple-500 hover:text-purple-500 transition-colors"
                >
                    <Plus size={16} />
                </button>
            </div>
        </div>
    );
}

/* =========================
   MAIN COMPONENT - KahootTaskCreator
========================= */
export default function KahootTaskCreator({
                                              defaultTopic = "",
                                              files = [],
                                          }: KahootTaskCreatorProps) {
    const [topic, setTopic] = useState(defaultTopic);
    const [context, setContext] = useState("");
    const [difficulty, setDifficulty] = useState("mittel");

    // Kahoot-spezifische Einstellungen
    const [numQuestions, setNumQuestions] = useState(10);
    const [timePerQuestion, setTimePerQuestion] = useState(20);
    const [pointsType, setPointsType] = useState("standard");
    const [includeImages, setIncludeImages] = useState(false);

    const [loading, setLoading] = useState(false);
    const [debugInfo, setDebugInfo] = useState<string>("");
    const [activeTab, setActiveTab] = useState<"config" | "preview" | "materials">("config");

    /* =========================
       Default Topic übernehmen
    ========================= */
    useEffect(() => {
        if (defaultTopic) {
            setTopic(defaultTopic);
        }
    }, [defaultTopic]);

    /* =========================
       Materialien extrahieren
    ========================= */
    const getAllMaterialText = async () => {
        if (!files.length) return "";

        let combinedText = "";
        let fileCount = 0;

        for (const file of files) {
            try {
                const storageRef = ref(storage, file.path);
                const url = await getDownloadURL(storageRef);
                const text = await extractTextFromFile(url, file.name);
                combinedText += `=== Datei: ${file.name} ===\n${text}\n\n`;
                fileCount++;
            } catch (err) {
                console.warn(
                    `Material konnte nicht gelesen werden: ${file.name}`,
                    err
                );
            }
        }

        console.log(`${fileCount} Dateien extrahiert, Länge: ${combinedText.length} Zeichen`);
        return combinedText;
    };

    /* =========================
       Generate Kahoot Quiz
    ========================= */
    const generateKahootQuiz = async () => {
        if (!topic.trim()) {
            alert("Bitte ein Thema angeben.");
            return;
        }

        setLoading(true);
        setDebugInfo("");

        try {
            const materialText = await getAllMaterialText();

            if (files.length > 0) {
                setDebugInfo(`Materialien geladen: ${materialText.length} Zeichen aus ${files.length} Dateien`);
            }

            const result: any = await httpsCallable(
                getFunctions(app),
                "generateKahoot"
            )({
                topic: topic.trim(),
                context: context.trim(),
                difficulty,
                materialText,
                numQuestions,
                timePerQuestion,
                pointsType,
                includeImages,
            });

            if (result.data?.file) {
                const blob = b64toBlob(result.data.file, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
                const url = URL.createObjectURL(blob);

                const a = document.createElement("a");
                a.href = url;
                a.download = `Kahoot_Quiz_${topic.replace(/[^a-z0-9]/gi, '_')}.xlsx`;
                a.click();

                URL.revokeObjectURL(url);

                setDebugInfo(`✅ Kahoot Quiz erfolgreich als Excel (XLSX) generiert und heruntergeladen!`);
                setTimeout(() => setDebugInfo(""), 5000);
            } else if (result.data?.error) {
                setDebugInfo(`❌ Fehler: ${result.data.error}`);
            } else {
                setDebugInfo("❌ Unbekannter Fehler beim Erstellen des Kahoot Quiz.");
            }
        } catch (err: any) {
            console.error("Fehler:", err);
            setDebugInfo(`❌ Serverfehler: ${err?.message || JSON.stringify(err)}`);
        }

        setLoading(false);
    };

    /* =========================
       Helpers
    ========================= */
    const b64toBlob = (base64: string, type: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        return new Blob([new Uint8Array(byteNumbers)], { type });
    };

    /* =========================
       Reset zu Standardwerten
    ========================= */
    const resetToDefaults = () => {
        setNumQuestions(10);
        setTimePerQuestion(20);
        setPointsType("standard");
        setIncludeImages(false);
        setDifficulty("mittel");
        setContext("");
        setDebugInfo("");
    };

    /* =========================
       Preview Content für Kahoot
    ========================= */
    const renderPreview = () => {
        const sourceInfo = files.length > 0
            ? `aus ${files.length} Material${files.length !== 1 ? 'ien' : ''}`
            : "basierend auf KI-Wissen";

        return (
            <div className="bg-gray-900/30 border border-gray-800 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-6">
                    <Trophy size={24} className="text-white" />
                    <h3 className="text-xl font-bold text-white">Vorschau deines Kahoot Quiz</h3>
                </div>

                <div className="space-y-6">
                    <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-6">
                        <h4 className="text-lg font-bold text-white mb-2">Kahoot Quiz: {topic || "[Thema]"}</h4>
                        <p className="text-gray-400 text-sm mb-6 pb-4 border-b border-gray-800">{sourceInfo}</p>

                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                            <div className="text-center">
                                <div className="text-3xl font-black text-purple-500">{numQuestions}</div>
                                <div className="text-sm text-gray-400">Fragen</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-purple-500">{timePerQuestion}s</div>
                                <div className="text-sm text-gray-400">Zeit pro Frage</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-purple-500">
                                    {pointsType === "standard" ? "Standard" : pointsType === "double" ? "Doppelt" : "Keine"}
                                </div>
                                <div className="text-sm text-gray-400">Punkte</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-black text-purple-500">{difficulty === "leicht" ? "Leicht" : difficulty === "mittel" ? "Mittel" : "Schwer"}</div>
                                <div className="text-sm text-gray-400">Schwierigkeit</div>
                            </div>
                        </div>

                        <div className="mb-6">
                            <h5 className="text-white font-semibold mb-3">Beispielfragen für Kahoot:</h5>
                            <div className="space-y-3">
                                <div className="bg-gray-900/30 p-4 rounded-lg">
                                    <span className="inline-block bg-purple-500/20 text-purple-500 text-xs font-semibold px-3 py-1 rounded-full mb-2">Frage 1</span>
                                    <p className="text-gray-300">Welche der fünf Säulen des Islam ist die wichtigste? (4 Antwortoptionen)</p>
                                </div>
                                <div className="bg-gray-900/30 p-4 rounded-lg">
                                    <span className="inline-block bg-purple-500/20 text-purple-500 text-xs font-semibold px-3 py-1 rounded-full mb-2">Frage 2</span>
                                    <p className="text-gray-300">Wie viele Propheten werden im Koran erwähnt? (4 Antwortoptionen)</p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-3 pt-4 border-t border-gray-800">
                            <div className="flex items-center gap-2 text-gray-400">
                                <FileSpreadsheet size={16} className="text-green-500" />
                                <span className="text-sm">Format: Excel XLSX (kompatibel mit Kahoot Import)</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-400">
                                <Gamepad2 size={16} className="text-purple-500" />
                                <span className="text-sm">Direkter Import in Kahoot möglich</span>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="flex items-center gap-3 text-gray-300">
                            <CheckCircle size={18} className="text-green-500" />
                            <span className="text-sm">Automatisch generierte Multiple-Choice Fragen</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                            <Timer size={18} className="text-blue-500" />
                            <span className="text-sm">Zeitlimit pro Frage: {timePerQuestion} Sekunden</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                            <Award size={18} className="text-purple-500" />
                            <span className="text-sm">{pointsType === "double" ? "Doppelte Punkte" : pointsType === "no_points" ? "Keine Punkte" : "Standard-Punkte"}</span>
                        </div>
                        <div className="flex items-center gap-3 text-gray-300">
                            <Hash size={18} className="text-yellow-500" />
                            <span className="text-sm">Didaktisch optimiert für den Unterricht</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    /* =========================
       Materials Content
    ========================= */
    const renderMaterials = () => (
        <div className="bg-gray-900/30 border border-gray-800 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-6">
                <BookOpen size={24} className="text-white" />
                <h3 className="text-xl font-bold text-white">Materialien</h3>
            </div>

            <div className="min-h-[200px]">
                {files.length > 0 ? (
                    <>
                        <div className="space-y-3 mb-6">
                            {files.map((file, index) => (
                                <div key={index} className="flex items-center gap-3 p-4 bg-gray-950/50 border border-gray-800 rounded-xl">
                                    <FileText size={18} className="text-gray-400" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-semibold text-white truncate">{file.name}</div>
                                        <div className="text-xs text-gray-500 truncate">{file.path}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="bg-gray-950/50 border border-gray-800 rounded-xl p-4">
                            <div className="flex gap-3">
                                <Sparkles size={20} className="text-purple-500 flex-shrink-0" />
                                <div>
                                    <div className="font-bold text-white">KI-gestütztes Kahoot Quiz</div>
                                    <p className="text-sm text-gray-400 mt-1">
                                        Das Quiz wird auf Basis dieser Materialien erstellt.
                                        Die KI analysiert den Inhalt und generiert Multiple-Choice Fragen.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12">
                        <BookOpen size={48} className="text-gray-500 mb-4" />
                        <p className="text-gray-400 text-center">
                            Keine Materialien hochgeladen. Die KI verwendet ihr Fachwissen.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    /* =========================
       RENDER
    ========================= */
    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-950 font-sans relative overflow-x-hidden">
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-r from-purple-500/30 to-transparent opacity-40 blur-[100px] animate-pulse-glow -z-10" />

            {/* HEADER */}
            <div className="bg-gray-900/80 backdrop-blur-lg border-b border-gray-800 relative z-10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-6">
                        <div className="bg-gradient-to-br from-purple-600 to-purple-800 w-16 h-16 rounded-2xl flex items-center justify-center text-white flex-shrink-0">
                            <Trophy size={32} />
                        </div>
                        <div className="flex-1">
                            <div className="inline-block bg-purple-500/15 text-purple-500 px-4 py-2 rounded-full text-sm font-semibold mb-4 border border-purple-500/30 backdrop-blur-sm">
                                🎮 KI-Kahoot-Generator
                            </div>
                            <h1 className="text-3xl md:text-4xl font-black text-white mb-3">
                                Erstelle <span className="bg-gradient-to-r from-purple-500 to-purple-800 bg-clip-text text-transparent">interaktive Kahoot Quizzes</span> für den Unterricht
                            </h1>
                            <p className="text-gray-300 text-lg">
                                Für den Islam-Unterricht – aus Texten und Materialien als Excel (XLSX) für Kahoot Import
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3">
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <Zap size={14} /> KI-gestützt
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <FileSpreadsheet size={14} /> Excel XLSX Export
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <Gamepad2 size={14} /> Kahoot kompatibel
                        </span>
                        <span className="inline-flex items-center gap-2 bg-gray-800/50 text-gray-300 px-4 py-2 rounded-full text-sm font-medium border border-gray-700">
                            <Target size={14} /> Lehrplankonform
                        </span>
                    </div>
                </div>
            </div>

            {/* MAIN CONTENT */}
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 relative z-10">
                {/* LEFT PANEL */}
                <div className="lg:col-span-2">
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl overflow-hidden">
                        {/* Tabs */}
                        <div className="flex flex-wrap gap-2 p-2 bg-gray-950/50 border-b border-gray-800">
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "config" ? "bg-purple-500/20 text-purple-500 border border-purple-500/30" : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"}`}
                                onClick={() => setActiveTab("config")}
                            >
                                <Settings size={18} />
                                Konfiguration
                            </button>
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "preview" ? "bg-purple-500/20 text-purple-500 border border-purple-500/30" : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"}`}
                                onClick={() => setActiveTab("preview")}
                            >
                                <Trophy size={18} />
                                Vorschau
                            </button>
                            <button
                                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 py-3 px-4 rounded-xl font-semibold transition-all ${activeTab === "materials" ? "bg-purple-500/20 text-purple-500 border border-purple-500/30" : "text-gray-500 hover:text-gray-300 hover:bg-gray-800/30"}`}
                                onClick={() => setActiveTab("materials")}
                            >
                                <BookOpen size={18} />
                                Materialien
                            </button>
                        </div>

                        {/* Tab Content */}
                        <div className="p-4 sm:p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
                            {activeTab === "config" ? (
                                <div className="space-y-8">
                                    {/* Thema & Kontext */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-purple-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-purple-500 flex-shrink-0">
                                                <BookOpen size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">Thema & Kontext</h3>
                                        </div>
                                        <div className="space-y-6">
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                                    Quiz-Thema *
                                                    <span className="block text-xs text-gray-500 font-normal mt-1">
                                                        Wichtig für präzise Kahoot-Fragen
                                                    </span>
                                                </label>
                                                <input
                                                    value={topic}
                                                    onChange={(e) => setTopic(e.target.value)}
                                                    placeholder="z. B. 'Fünf Säulen des Islam', 'Ramadan', 'Propheten'"
                                                    className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-semibold text-gray-300 mb-2">
                                                    Zusätzlicher Kontext
                                                    <span className="block text-xs text-gray-500 font-normal mt-1">
                                                        Optional: Klassenstufe, Lernziele, etc.
                                                    </span>
                                                </label>
                                                <textarea
                                                    value={context}
                                                    onChange={(e) => setContext(e.target.value)}
                                                    placeholder="z. B. 'Klassenstufe 7', 'Wiederholungsquiz', 'Vorbereitung auf Klassenarbeit'"
                                                    className="w-full px-4 py-3 bg-gray-900/50 border-2 border-gray-800 rounded-xl text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/30 min-h-[100px] resize-y"
                                                />
                                            </div>
                                        </div>
                                    </section>

                                    {/* Kahoot-Einstellungen */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-purple-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-purple-500 flex-shrink-0">
                                                <Trophy size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">Kahoot Einstellungen</h3>
                                        </div>

                                        {/* Anzahl der Fragen */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-semibold text-gray-300 mb-3">
                                                Anzahl der Fragen *
                                                <span className="block text-xs text-gray-500 font-normal mt-1">
                                                    Zwischen 1 und 20 Fragen (empfohlen: 10-15)
                                                </span>
                                            </label>
                                            <QuestionTypeCard
                                                label="Quiz-Fragen"
                                                description="Multiple-Choice Fragen mit 4 Antwortmöglichkeiten"
                                                value={numQuestions}
                                                onChange={setNumQuestions}
                                                color="#9400D3"
                                                icon="❓"
                                            />
                                        </div>

                                        {/* Zeit pro Frage */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-semibold text-gray-300 mb-3">
                                                Zeit pro Frage (Sekunden)
                                                <span className="block text-xs text-gray-500 font-normal mt-1">
                                                    Standard-Kahoot Zeitlimits
                                                </span>
                                            </label>
                                            <div className="flex flex-wrap gap-2">
                                                {[5, 10, 20, 30, 60, 90, 120].map((time) => (
                                                    <button
                                                        key={time}
                                                        className={`px-4 py-2 border-2 rounded-lg font-medium transition-all ${timePerQuestion === time ? "border-purple-500 bg-purple-500/10 text-purple-500" : "border-gray-700 bg-gray-800/30 text-gray-400 hover:border-gray-600"}`}
                                                        onClick={() => setTimePerQuestion(time)}
                                                    >
                                                        {time}s
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Punktesystem */}
                                        <div className="mb-6">
                                            <label className="block text-sm font-semibold text-gray-300 mb-3">
                                                Punktesystem
                                                <span className="block text-xs text-gray-500 font-normal mt-1">
                                                    Wie werden Punkte vergeben?
                                                </span>
                                            </label>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <button
                                                    className={`flex-1 py-3 px-4 border-2 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${pointsType === "standard" ? "border-purple-500 bg-purple-500/10 text-purple-500" : "border-gray-700 bg-gray-800/30 text-gray-400 hover:border-gray-600"}`}
                                                    onClick={() => setPointsType("standard")}
                                                >
                                                    <Award size={16} /> Standard
                                                </button>
                                                <button
                                                    className={`flex-1 py-3 px-4 border-2 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${pointsType === "double" ? "border-purple-500 bg-purple-500/10 text-purple-500" : "border-gray-700 bg-gray-800/30 text-gray-400 hover:border-gray-600"}`}
                                                    onClick={() => setPointsType("double")}
                                                >
                                                    <Award size={16} /> Doppelte Punkte
                                                </button>
                                                <button
                                                    className={`flex-1 py-3 px-4 border-2 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${pointsType === "no_points" ? "border-purple-500 bg-purple-500/10 text-purple-500" : "border-gray-700 bg-gray-800/30 text-gray-400 hover:border-gray-600"}`}
                                                    onClick={() => setPointsType("no_points")}
                                                >
                                                    <Award size={16} /> Keine Punkte
                                                </button>
                                            </div>
                                        </div>

                                        {/* Optionen */}
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-300 mb-3">
                                                Optionen
                                            </label>
                                            <div className="flex items-center gap-3 p-4 bg-gray-900/30 rounded-xl hover:bg-gray-800/30 transition-colors cursor-pointer" onClick={() => setIncludeImages(!includeImages)}>
                                                <div className={`w-6 h-6 rounded flex items-center justify-center ${includeImages ? "bg-purple-500" : "border-2 border-gray-600"}`}>
                                                    {includeImages && <Check size={14} className="text-white" />}
                                                </div>
                                                <span className="text-gray-300 text-sm">Bildvorschläge für Fragen einfügen (experimentell)</span>
                                            </div>
                                        </div>
                                    </section>

                                    {/* Schwierigkeitsgrad */}
                                    <section>
                                        <div className="flex items-center gap-3 mb-6">
                                            <div className="bg-purple-500/10 w-10 h-10 rounded-xl flex items-center justify-center text-purple-500 flex-shrink-0">
                                                <TrendingUp size={20} />
                                            </div>
                                            <h3 className="text-xl font-bold text-white">Schwierigkeitsgrad</h3>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3 mb-4">
                                            {["leicht", "mittel", "schwer"].map((level) => (
                                                <button
                                                    key={level}
                                                    className={`flex-1 py-3 px-4 border-2 rounded-xl font-semibold transition-all ${difficulty === level ? "border-purple-500 bg-purple-500/10 text-purple-500" : "border-gray-700 bg-gray-800/30 text-gray-400 hover:border-gray-600"}`}
                                                    onClick={() => setDifficulty(level)}
                                                >
                                                    {level === "leicht" && "👶 Leicht"}
                                                    {level === "mittel" && "🎯 Mittel"}
                                                    {level === "schwer" && "🧠 Schwer"}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="text-sm text-gray-400 p-4 bg-gray-900/30 rounded-xl">
                                            {difficulty === "leicht" && (
                                                <span>Einfache Fragen, Basiswissen, klare Antworten</span>
                                            )}
                                            {difficulty === "mittel" && (
                                                <span>Anspruchsvolle Fragen, Transferwissen, knifflige Optionen</span>
                                            )}
                                            {difficulty === "schwer" && (
                                                <span>Komplexe Fragen, detailliertes Wissen, ähnliche Antwortoptionen</span>
                                            )}
                                        </div>
                                    </section>
                                </div>
                            ) : activeTab === "preview" ? (
                                renderPreview()
                            ) : (
                                renderMaterials()
                            )}
                        </div>
                    </div>
                </div>

                {/* RIGHT PANEL */}
                <div className="space-y-6">
                    {/* Action Card */}
                    <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-6">
                        <h3 className="text-xl font-bold text-white mb-6">Generierung starten</h3>

                        <div className="space-y-4 mb-6 p-4 bg-gray-900/30 rounded-xl">
                            <div className="flex justify-between">
                                <span className="text-gray-400">Thema:</span>
                                <strong className="text-white">{topic || "Nicht angegeben"}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Format:</span>
                                <strong className="px-3 py-1 rounded-full text-xs font-bold bg-green-500/20 text-green-500 border border-green-500/30">
                                    Excel XLSX
                                </strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Fragen:</span>
                                <strong className="text-white">{numQuestions}</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Zeit pro Frage:</span>
                                <strong className="text-white">{timePerQuestion} Sekunden</strong>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-400">Materialien:</span>
                                <strong className="text-white">{files.length} Datei{files.length !== 1 ? 'en' : ''}</strong>
                            </div>
                        </div>

                        <button
                            onClick={generateKahootQuiz}
                            disabled={loading || !topic.trim()}
                            className={`w-full py-4 px-6 rounded-xl font-bold text-white transition-all flex items-center justify-center gap-3 ${loading ? "opacity-80 cursor-wait" : ""} ${!topic.trim() ? "opacity-50 cursor-not-allowed" : "bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 hover:-translate-y-1 hover:shadow-2xl hover:shadow-purple-500/40"}`}
                        >
                            {loading ? (
                                <>
                                    <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                    Generiere Kahoot Excel...
                                </>
                            ) : (
                                <>
                                    <Download size={20} />
                                    Kahoot Excel (XLSX) generieren & herunterladen
                                </>
                            )}
                        </button>

                        {!topic.trim() && (
                            <div className="text-red-500 text-sm text-center mt-3">
                                Bitte gib ein Thema an, um ein Kahoot Quiz zu generieren.
                            </div>
                        )}

                        <button
                            onClick={resetToDefaults}
                            className="w-full mt-4 py-3 px-4 border-2 border-gray-700 rounded-xl text-gray-400 font-semibold hover:border-purple-500 hover:text-purple-500 transition-all flex items-center justify-center gap-2"
                        >
                            <Settings size={16} />
                            Zurücksetzen
                        </button>
                    </div>

                    {/* Debug Info */}
                    {debugInfo && (
                        <div className="bg-gray-900/80 backdrop-blur-lg border border-gray-800 rounded-2xl p-5">
                            <div className="flex justify-between items-center mb-3">
                                <span className="text-sm font-semibold text-gray-400">Status</span>
                                {debugInfo.includes("✅") && <Check size={16} className="text-green-500" />}
                                {debugInfo.includes("❌") && <X size={16} className="text-red-500" />}
                                {debugInfo.includes("⚠️") && <AlertCircle size={16} className="text-yellow-500" />}
                            </div>
                            <div className="text-sm break-words">
                                {debugInfo.includes("✅") ? (
                                    <span className="text-green-500 font-semibold">{debugInfo}</span>
                                ) : debugInfo.includes("❌") ? (
                                    <span className="text-red-500 font-semibold">{debugInfo}</span>
                                ) : debugInfo.includes("⚠️") ? (
                                    <span className="text-yellow-500 font-semibold">{debugInfo}</span>
                                ) : (
                                    <span className="text-gray-300">{debugInfo}</span>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tips */}
                    <div className="bg-gradient-to-br from-purple-500/10 to-purple-800/10 border border-purple-500/20 rounded-2xl p-6">
                        <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-3">
                            <Sparkles size={18} className="text-purple-500" />
                            Tipps für Kahoot Quizzes
                        </h4>
                        <ul className="space-y-3 text-gray-300">
                            <li className="flex items-start gap-2">
                                <span className="text-purple-500">•</span>
                                <span className="text-sm">10-15 Fragen sind ideal für eine Unterrichtseinheit</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-purple-500">•</span>
                                <span className="text-sm">20 Sekunden pro Frage ist ein guter Standardwert</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-purple-500">•</span>
                                <span className="text-sm">Excel XLSX-Datei kann direkt in Kahoot importiert werden</span>
                            </li>
                            <li className="flex items-start gap-2">
                                <span className="text-purple-500">•</span>
                                <span className="text-sm">Für jüngere Schüler: längere Zeit, für ältere: kürzere Zeit</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* LOADING OVERLAY */}
            {loading && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-gradient-to-br from-gray-900 to-gray-950 p-8 rounded-3xl text-center max-w-md w-full mx-4 border border-gray-800 animate-fade-in">
                        <div className="w-16 h-16 border-4 border-gray-700 border-t-purple-500 rounded-full animate-spin mx-auto mb-6" />
                        <h3 className="text-2xl font-bold text-white mb-3">KI generiert dein Kahoot Quiz</h3>
                        <p className="text-gray-400 mb-6">
                            Dies kann 15-30 Sekunden dauern.
                            Format: Excel XLSX (Kahoot Import)
                        </p>
                        <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
                            <div className="h-full bg-gradient-to-r from-purple-500 to-purple-700 w-1/2 animate-pulse" />
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}